import { useEffect, useRef, useState } from "react";
import { api, API_URL } from "@/lib/api";
import { usePortalAuth } from "@/lib/portalAuth";
import { Sparkles, Send, RotateCcw, X, Zap, Check, Loader2 } from "lucide-react";
import { parseAiMessage, lastAiStatus } from "@/components/AiAssistant";

const newSession = () => {
  const sid = `pai-${crypto.randomUUID()}`;
  localStorage.setItem("portal_ai_session", sid);
  return sid;
};

const SUGGESTIONS = [
  "Where is my order right now?",
  "Is ID 8mm x OD 12mm tube available in stock?",
  "What's my outstanding balance?",
  "Which silicone tube is best for food-grade use?",
  "I'd like a quote for silicone sponge cord",
];

export const PortalAiAssistant = ({ open, onClose }) => {
  const { customer } = usePortalAuth();
  const [session, setSession] = useState(() => localStorage.getItem("portal_ai_session") || newSession());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [actBusy, setActBusy] = useState(false);
  const [dismissed, setDismissed] = useState(() => new Set());
  const endRef = useRef(null);

  useEffect(() => {
    if (open) api.get(`/portal/ai/history/${session}`).then((r) => setMessages(r.data)).catch(() => {});
  }, [open, session]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (preset) => {
    const text = (preset || input).trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API_URL}/portal/ai/chat`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: session, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("AI request failed");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = dec.decode(value);
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: `Sorry — ${e.message}` };
        return copy;
      });
    }
    setBusy(false);
  };

  const runAction = async (action) => {
    if (actBusy) return;
    setActBusy(true);
    try {
      const r = await api.post("/portal/ai/execute", { session_id: session, action: action.action, params: action.params || {} });
      setMessages((m) => [...m, { role: "assistant", content: r.data.message }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: `⚠️ ${e.response?.data?.detail || e.message}` }]);
    }
    setActBusy(false);
  };

  const reset = () => { setSession(newSession()); setMessages([]); setDismissed(new Set()); };
  const firstName = (customer?.contact_person || customer?.company_name || "").split(" ")[0];

  if (!open) return null;
  return (
    <div className="fixed bottom-5 right-5 z-50 w-[92vw] max-w-[360px] h-[500px] max-h-[75vh] bg-white rounded-2xl border border-[#3B82F6]/10 shadow-2xl flex flex-col overflow-hidden fade-up" data-testid="portal-ai-panel">
      <div className="flex items-center justify-between px-4 py-3 bg-[#3B82F6] text-white flex-none">
        <div className="flex items-center gap-2 text-sm font-semibold"><Sparkles size={15} className="text-[#C4B5FD]" /> Hi-Tech Assistant</div>
        <div className="flex items-center gap-1">
          <button onClick={reset} className="p-1.5 rounded hover:bg-white/10" title="New chat" data-testid="portal-ai-new"><RotateCcw size={14} /></button>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/10" data-testid="portal-ai-close"><X size={16} /></button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F7F5FC]">
        {messages.length === 0 && (
          <div className="text-xs text-slate-500 space-y-2">
            <div className="bg-white border border-[#3B82F6]/10 rounded-xl px-3.5 py-3 text-[13px] leading-relaxed text-slate-700">
              Hi{firstName ? ` ${firstName}` : ""} 👋 I'm your Hi-Tech Silico assistant. I can track your orders,
              check stock availability & balances, and even send a quote request for you. How can I help?
            </div>
            <p className="font-semibold text-slate-600 pt-1">Quick questions:</p>
            {SUGGESTIONS.map((q) => (
              <button key={q} onClick={() => send(q)} data-testid="portal-ai-suggestion"
                      className="block w-full text-left px-3 py-2 rounded-lg border border-[#3B82F6]/10 bg-white hover:border-[#8B5CF6]/60 hover:text-[#3B82F6] transition-colors duration-200">{q}</button>
            ))}
          </div>
        )}
        {messages.map((m, i) => {
          const { text, action } = m.role === "assistant" ? parseAiMessage(m.content) : { text: m.content, action: null };
          const isLast = i === messages.length - 1;
          return (
            <div key={i} className="space-y-2">
              {(text || !action) && (
                <div className={`max-w-[88%] rounded-xl px-3.5 py-2.5 text-[13px] leading-relaxed whitespace-pre-wrap ${
                  m.role === "user" ? "ml-auto bg-[#3B82F6] text-white" : "bg-white border border-[#3B82F6]/10 text-slate-800"}`}>
                  {text || (busy && isLast ? (
                    <span className="flex flex-col gap-1" data-testid="portal-ai-typing-indicator">
                      <span className="inline-flex items-center gap-1 py-0.5 text-slate-400">
                        <span className="ai-typing-dot" />
                        <span className="ai-typing-dot" style={{ animationDelay: "0.18s" }} />
                        <span className="ai-typing-dot" style={{ animationDelay: "0.36s" }} />
                      </span>
                      {lastAiStatus(m.content) && (
                        <span className="text-[11px] text-slate-400 italic" data-testid="portal-ai-tool-status">{lastAiStatus(m.content)}</span>
                      )}
                    </span>
                  ) : "")}
                </div>
              )}
              {action && (isLast && !busy && !dismissed.has(i) ? (
                <div className="max-w-[88%] rounded-xl border border-[#8B5CF6]/40 bg-white p-3 shadow-sm" data-testid="portal-ai-action-card">
                  <div className="flex items-start gap-2 text-[13px] text-slate-800">
                    <Zap size={14} className="text-[#8B5CF6] mt-0.5 flex-none" />
                    <span className="font-medium">{action.summary || "Send this request?"}</span>
                  </div>
                  <div className="flex gap-2 mt-2.5">
                    <button onClick={() => runAction(action)} disabled={actBusy} data-testid="portal-ai-action-confirm"
                            className="flex-1 h-8 rounded-full bg-[#3B82F6] text-white text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50 hover:bg-[#2563EB] transition-colors duration-200">
                      {actBusy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Confirm
                    </button>
                    <button onClick={() => setDismissed((s) => new Set(s).add(i))} disabled={actBusy} data-testid="portal-ai-action-cancel"
                            className="px-4 h-8 rounded-full border border-slate-300 text-xs text-slate-600 hover:bg-slate-50 transition-colors duration-200">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 text-[11px] text-slate-400 px-1" data-testid="portal-ai-action-chip">
                  <Zap size={11} className="flex-none" /> {action.summary}
                </div>
              ))}
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="p-3 border-t border-[#3B82F6]/10 flex gap-2 flex-none bg-white">
        <input value={input} onChange={(e) => setInput(e.target.value)}
               onKeyDown={(e) => e.key === "Enter" && send()}
               placeholder="Ask about orders, stock, products…"
               className="flex-1 text-sm border border-[#3B82F6]/15 rounded-full px-4 py-2.5 outline-none focus:border-[#8B5CF6]"
               data-testid="portal-ai-input" />
        <button onClick={() => send()} disabled={busy || !input.trim()}
                className="w-10 h-10 rounded-full bg-[#3B82F6] text-white flex items-center justify-center disabled:opacity-40 hover:bg-[#2563EB] transition-colors duration-200"
                data-testid="portal-ai-send"><Send size={15} /></button>
      </div>
    </div>
  );
};
